/**
 * Which chats have a reply being written right now (or waiting for a tool approval), for the
 * sidebar. Loaded from the server when the event connection starts or comes back, then kept up to
 * date by the gen.* events (lib/events.ts).
 */
import { create } from "zustand";
import { api } from "./api";
import type { Part } from "./types";
import { clientId, type ServerEvent } from "./ws";

export type Activity = "working" | "approval";

interface Running {
  chatId?: string | null;
  tempId?: string | null;
  approval: boolean;
}

interface RunningStore {
  byMessage: Record<string, Running>;
  apply: (ev: ServerEvent) => void;
  load: () => Promise<void>;
}

const waitsForApproval = (parts: Part[] | undefined) =>
  !!parts?.some((p) => p.type === "tool_call" && p.status === "awaiting_approval");

export const useRunning = create<RunningStore>((set) => ({
  byMessage: {},
  apply: (ev) => {
    const id: string = ev.message_id;
    if (!id) return;
    set((s) => {
      const byMessage = { ...s.byMessage };
      if (ev.type === "gen.done") delete byMessage[id];
      else {
        const cur = byMessage[id];
        const approval = ev.type === "gen.parts" ? waitsForApproval(ev.parts) : (cur?.approval ?? false);
        byMessage[id] = { chatId: ev.chat_id ?? cur?.chatId, tempId: ev.temp_id ?? cur?.tempId, approval };
      }
      return { byMessage };
    });
  },
  load: async () => {
    try {
      const list = await api.get<{ message_id: string; chat_id: string | null; temp_id: string | null; approval: boolean }[]>(`/api/generations?client=${clientId()}`);
      set({ byMessage: Object.fromEntries(list.map((g) => [g.message_id, { chatId: g.chat_id, tempId: g.temp_id, approval: g.approval }])) });
    } catch {
      /* keep what we have */
    }
  },
}));

/** What a chat (or temporary chat, by its id) is doing, if anything. */
export function useActivity(id: string): Activity | null {
  return useRunning((s) => {
    let found: Activity | null = null;
    for (const r of Object.values(s.byMessage)) {
      if (r.chatId !== id && r.tempId !== id) continue;
      if (r.approval) return "approval";
      found = "working";
    }
    return found;
  });
}
